import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiHelpCircle, FiMessageCircle, FiBookOpen, FiPlayCircle, FiStar, FiCompass, FiMessageSquare, FiChevronDown
} from 'react-icons/fi';
import HelpCenter from './HelpCenter.jsx';
import './help.css';

/**
 * Header "Help" menu used on every signed-in page: ask NovaBotX how to use it, browse topics,
 * watch the demo, see what's new, take the tour, or send feedback.
 *
 * On the chat page, pass onAsk / onFeedback / onOpenCenter so it acts in place.
 * Elsewhere those default to opening the chat with the question or the feedback dialog.
 */
export default function HelpMenu({ onTour, onAsk, onFeedback, onOpenCenter }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [centerTab, setCenterTab] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const ask = onAsk || ((question) => navigate('/chat', { state: { helpQuestion: question } }));
  const feedback = onFeedback || (() => navigate('/chat', { state: { openFeedback: true } }));
  const openCenter = onOpenCenter || setCenterTab;

  const run = (action) => () => {
    setOpen(false);
    action();
  };

  const items = [
    { icon: FiMessageCircle, label: 'Ask how to use NovaBotX', action: () => ask('How do I use NovaBotX?') },
    { icon: FiBookOpen, label: 'Browse help topics', action: () => openCenter('topics') },
    { icon: FiPlayCircle, label: 'Watch the demo', action: () => openCenter('demo') },
    { icon: FiStar, label: "What's new", action: () => openCenter('whats-new') },
    ...(onTour ? [{ icon: FiCompass, label: 'Take the tour of this page', action: onTour }] : []),
    { icon: FiMessageSquare, label: 'Send feedback', action: feedback }
  ];

  return (
    <div className="nb-help-menu" ref={rootRef}>
      <button
        type="button"
        className="nb-tour-trigger nb-help-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FiHelpCircle aria-hidden="true" /> <span>Help</span>
        <FiChevronDown aria-hidden="true" className="nb-help-caret" />
      </button>
      {open && (
        <div className="nb-help-pop" role="menu">
          {items.map(({ icon: Icon, label, action }) => (
            <button key={label} type="button" role="menuitem" onClick={run(action)}>
              <Icon aria-hidden="true" /> {label}
            </button>
          ))}
        </div>
      )}
      {!onOpenCenter && centerTab && (
        <HelpCenter tab={centerTab} onTabChange={setCenterTab} onAsk={ask} onClose={() => setCenterTab(null)} />
      )}
    </div>
  );
}

/** Always-visible feedback button for page headers. */
export function FeedbackButton({ onClick }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="nb-tour-trigger nb-feedback-trigger"
      onClick={onClick || (() => navigate('/chat', { state: { openFeedback: true } }))}
      aria-label="Send feedback"
    >
      <FiMessageSquare aria-hidden="true" /> <span>Feedback</span>
    </button>
  );
}
